import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./ImageTools.css";

type Mode = "compressor" | "resizer" | "converter";
const names = { compressor: "Image Compressor", resizer: "Image Resizer", converter: "Image Converter" };
const descriptions = {
  compressor: "Make photos lighter for your website, emails, and everyday sharing.",
  resizer: "Give your image the exact dimensions you need.",
  converter: "Switch between JPEG, PNG, and WebP in a few clicks.",
};
const size = (bytes: number) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(2)} MB`;
interface Source { file: File; image: HTMLImageElement; url: string }
interface Result { url: string; name: string; bytes: number; width: number; height: number }

export default function ImageTools({ mode }: { mode: Mode }) {
  const [source, setSource] = useState<Source | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [format, setFormat] = useState(mode === "compressor" ? "image/webp" : "image/png");
  const [quality, setQuality] = useState(80);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [locked, setLocked] = useState(true);
  const generation = useRef(0);
  useEffect(() => () => { generation.current++; }, []);
  useEffect(() => () => { if (source) URL.revokeObjectURL(source.url); }, [source]);
  useEffect(() => () => { if (result) URL.revokeObjectURL(result.url); }, [result]);

  async function choose(file?: File) {
    if (!file) return;
    const token = ++generation.current;
    setSource(null); setResult(null); setError(""); setBusy(true);
    let url = "";
    try {
      if (!file.size || file.size > 20 * 1024 * 1024) throw new Error("Choose an image between 1 byte and 20 MB.");
      if (!/\.(jpe?g|png|webp)$/i.test(file.name) && !/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error("Choose a JPEG, PNG, or WebP image. Export HEIC photos as JPEG first.");
      url = URL.createObjectURL(file);
      const image = new Image();
      image.src = url;
      await image.decode();
      if (image.naturalWidth * image.naturalHeight > 16000000 || Math.max(image.naturalWidth, image.naturalHeight) > 8192) throw new Error("Choose an image up to 16 megapixels and 8,192 pixels per side.");
      if (token !== generation.current) { URL.revokeObjectURL(url); return; }
      setSource({ file, image, url });
      setWidth(String(image.naturalWidth)); setHeight(String(image.naturalHeight));
    } catch (e) {
      if (url) URL.revokeObjectURL(url);
      if (token === generation.current) setError(e instanceof Error && e.name !== "EncodingError" ? e.message : "This image could not be opened. Try exporting it as JPEG or PNG.");
    } finally { if (token === generation.current) setBusy(false); }
  }
  function dimension(value: string, axis: "width" | "height") {
    setResult(null);
    if (axis === "width") setWidth(value); else setHeight(value);
    if (locked && source && Number(value) > 0) {
      const ratio = source.image.naturalWidth / source.image.naturalHeight;
      if (axis === "width") setHeight(String(Math.max(1, Math.round(Number(value) / ratio))));
      else setWidth(String(Math.max(1, Math.round(Number(value) * ratio))));
    }
  }
  async function process() {
    if (!source) return;
    const token = ++generation.current;
    setBusy(true); setError(""); setResult(null);
    try {
      const w = mode === "resizer" ? Number(width) : source.image.naturalWidth;
      const h = mode === "resizer" ? Number(height) : source.image.naturalHeight;
      if (![w, h].every(n => Number.isInteger(n) && n > 0 && n <= 8192) || w * h > 16000000) throw new Error("Use whole-number dimensions from 1 to 8,192, with no more than 16 million pixels total.");
      const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Your browser could not process this image.");
      if (format === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); }
      ctx.imageSmoothingQuality = "high"; ctx.drawImage(source.image, 0, 0, w, h);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, format, quality / 100));
      canvas.width = 0; canvas.height = 0;
      if (!blob || blob.type !== format) throw new Error("Your browser does not support this output format. Choose PNG or JPEG.");
      if (token !== generation.current) return;
      const extension = format === "image/jpeg" ? "jpg" : format.split("/")[1];
      setResult({ url: URL.createObjectURL(blob), name: `${source.file.name.replace(/\.[^.]+$/, "") || "image"}-${mode}.${extension}`, bytes: blob.size, width: w, height: h });
    } catch (e) { if (token === generation.current) setError(e instanceof Error ? e.message : "Unable to process this image. Try a smaller file."); }
    finally { if (token === generation.current) setBusy(false); }
  }
  return <section className="ed-page image-tools"><div className="ed-wrap">
    <Link to="/smart-tools" className="image-back">← All tools</Link>
    <p className="image-eyebrow">THE EVERYDAY TOOLKIT / IMAGES</p>
    <h1>{names[mode]}</h1><p className="image-intro">{descriptions[mode]}</p>
    <p className="image-private">Processed on your device. Your images are never uploaded.</p>
    <div className="image-workspace">
      <div className="image-controls">
        <h2>1. Choose your image</h2>
        <label htmlFor="image-file">JPEG, PNG, or WebP · up to 20 MB / 16 megapixels</label>
        <input id="image-file" type="file" disabled={busy} onChange={e => { const file = e.target.files?.[0]; e.target.value = ""; void choose(file); }} />
        {source && <p className="image-selected" role="status">Selected: <strong>{source.file.name}</strong><br />{source.image.naturalWidth} × {source.image.naturalHeight} px · {size(source.file.size)}</p>}
        <h2>2. Adjust & export</h2>
        <fieldset disabled={busy}><legend className="image-sr">Export settings</legend>
          {mode === "resizer" && <><div className="image-dimensions">
            <label>Width (px)<input type="number" min="1" max="8192" value={width} onChange={e => dimension(e.target.value, "width")} /></label>
            <label>Height (px)<input type="number" min="1" max="8192" value={height} onChange={e => dimension(e.target.value, "height")} /></label>
          </div><label className="image-lock"><input type="checkbox" checked={locked} onChange={e => { setLocked(e.target.checked); setResult(null); if (e.target.checked && source) setHeight(String(Math.max(1, Math.round(Number(width) * source.image.naturalHeight / source.image.naturalWidth)))); }} /> Keep original proportions</label></>}
          <label>Output format<select value={format} onChange={e => { setFormat(e.target.value); setResult(null); }}>
            <option value="image/jpeg">JPEG — photos</option><option value="image/webp">WebP — smaller web images</option>{mode !== "compressor" && <option value="image/png">PNG — lossless, transparency</option>}
          </select></label>
          {format !== "image/png" && <label>Quality: {quality}%<input type="range" min="10" max="100" value={quality} onChange={e => { setQuality(Number(e.target.value)); setResult(null); }} /></label>}
          <p className="image-note">JPEG replaces transparency with white. Animated images become a still image. Output size depends on the image and settings.</p>
          <button className="ed-button" disabled={!source || busy} onClick={() => void process()}>{busy ? "Processing…" : mode === "compressor" ? "Compress image ↗" : mode === "resizer" ? "Resize image ↗" : "Convert image ↗"}</button>
        </fieldset>
        {error && <p className="image-error" role="alert">{error}</p>}
      </div>
      <div className="image-preview" aria-live="polite">
        {source ? <><img src={result?.url || source.url} alt={result ? "Processed image preview" : "Selected image preview"} />
          {result ? <div className="image-result"><h2>Your image is ready</h2><p>{result.width} × {result.height} px · {size(result.bytes)}</p><p>{result.bytes < source.file.size ? `${Math.round((1 - result.bytes / source.file.size) * 100)}% smaller than the original.` : "This output is not smaller than the original. Try a lower quality or WebP."}</p><a className="ed-button" href={result.url} download={result.name}>Download image ↓</a></div> : <p>Original preview · choose your settings, then export.</p>}</> : <div className="image-empty"><span aria-hidden="true">▧</span><h2>A little lighter. Just the right size.</h2><p>Your image preview and download will appear here.</p></div>}
      </div>
    </div>
    <nav className="image-related" aria-label="More image tools">{(Object.keys(names) as Mode[]).filter(item => item !== mode).map(item => <Link key={item} to={`/image-${item}`}>{names[item]} ↗</Link>)}</nav>
  </div></section>;
}
